<script setup lang="ts">
  import { IconCheck } from '@gat-ship/icons'
  import {
    ModalSectionHeader,
    ModalSectionHeaderTitle
  } from '@gat-ship/vue-components'

  /** One recap line: a step's label and its chosen value (or '' when unset). */
  export interface SummaryRow {
    key: string
    label: string
    value: string
  }

  defineProps<{
    rows: SummaryRow[]
    /** True once the port call has been created (shows the success banner). */
    done: boolean
  }>()
</script>

<template>
  <div class="flex min-h-0 flex-1 flex-col">
    <div class="flex-none px-6 pb-4 pt-6">
      <ModalSectionHeader class="gap-0">
        <div
          class="text-muted-foreground mb-2 text-[11px] font-semibold uppercase tracking-wider"
        >
          Review
        </div>
        <ModalSectionHeaderTitle class="text-lg font-semibold leading-tight">
          {{ done ? 'Port call created' : 'Review & create' }}
        </ModalSectionHeaderTitle>
      </ModalSectionHeader>
    </div>

    <div class="min-h-0 flex-1 overflow-auto px-6 pb-6">
      <!-- success banner: shown after the call is created -->
      <div
        v-if="done"
        class="mb-5 flex items-start gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3.5"
      >
        <span
          class="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-emerald-500/15"
        >
          <IconCheck class="size-3.5 text-emerald-600 dark:text-emerald-400" />
        </span>
        <div>
          <div
            class="text-sm font-semibold text-emerald-700 dark:text-emerald-300"
          >
            Port call created
          </div>
          <div class="text-muted-foreground mt-0.5 text-xs leading-relaxed">
            Registered with status Pre-Arrival and vessel call status Expected.
          </div>
        </div>
      </div>

      <!-- recap of every answer -->
      <dl class="divide-border bg-card divide-y rounded-lg border">
        <div
          v-for="row in rows"
          :key="row.key"
          class="flex items-baseline justify-between gap-4 px-4 py-2.5"
        >
          <dt class="text-muted-foreground text-[13px] font-medium">
            {{ row.label }}
          </dt>
          <dd
            class="text-foreground max-w-[60%] truncate text-[13px] font-medium"
          >
            <template v-if="row.value">{{ row.value }}</template>
            <span v-else class="text-muted-foreground/50">Not set</span>
          </dd>
        </div>
      </dl>

      <p
        v-if="!done"
        class="text-muted-foreground mt-4 text-xs leading-relaxed"
      >
        Check the details above, then create the port call.
      </p>
    </div>
  </div>
</template>
